/** Session identity. Acquisition fields live in acquisition.ts. */

import { getNamespace } from "@/namespace";
import { isLocalStorageAvailable, localStorage, sessionStorage } from "@/utils";

import { type Acquisition } from "./acquisition";
import { uuidv7 } from "./uuid";

export const SESSION_IDLE_MS = 30 * 60 * 1000;

export interface StoredSession {
  id: string;
  startedAt: number;
  lastActivityAt: number;
  campaignKey: string;
  landingHost: string;
  landingPath: string;
  referrerHost: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  clickIdType: string;
  clickId: string;
  /** Set once `widget_loaded` has been queued. Survives a full page load. */
  widgetLoaded?: boolean;
}

function sessionKey(siteSlug: string): string {
  return `${getNamespace()}_session_${siteSlug}`;
}

function durable() {
  return isLocalStorageAvailable() ? localStorage : sessionStorage;
}

export function shouldStartNewSession(
  stored: { lastActivityAt: number; campaignKey: string } | null,
  now: number,
  campaignKey: string,
): boolean {
  if (!stored) return true;
  if (now - stored.lastActivityAt > SESSION_IDLE_MS) return true;
  if (campaignKey !== "" && campaignKey !== stored.campaignKey) return true;
  return false;
}

export function readSession(siteSlug: string): StoredSession | null {
  const raw = durable().getJSON<Partial<StoredSession>>(sessionKey(siteSlug));
  if (!raw || typeof raw.id !== "string" || typeof raw.startedAt !== "number") {
    return null;
  }
  return {
    id: raw.id,
    startedAt: raw.startedAt,
    lastActivityAt:
      typeof raw.lastActivityAt === "number"
        ? raw.lastActivityAt
        : raw.startedAt,
    campaignKey: raw.campaignKey ?? "",
    landingHost: raw.landingHost ?? "",
    landingPath: raw.landingPath ?? "/",
    referrerHost: raw.referrerHost ?? "",
    utmSource: raw.utmSource ?? "",
    utmMedium: raw.utmMedium ?? "",
    utmCampaign: raw.utmCampaign ?? "",
    clickIdType: raw.clickIdType ?? "",
    clickId: raw.clickId ?? "",
    widgetLoaded: raw.widgetLoaded === true,
  };
}

export function writeSession(siteSlug: string, session: StoredSession): void {
  durable().setJSON(sessionKey(siteSlug), session);
}

export function createSession(
  now: number,
  acquisition: Acquisition,
): StoredSession {
  return {
    id: uuidv7(now),
    startedAt: now,
    lastActivityAt: now,
    campaignKey: acquisition.campaignKey,
    landingHost: acquisition.landingHost,
    landingPath: acquisition.landingPath,
    referrerHost: acquisition.referrerHost,
    utmSource: acquisition.utmSource,
    utmMedium: acquisition.utmMedium,
    utmCampaign: acquisition.utmCampaign,
    clickIdType: acquisition.clickIdType,
    clickId: acquisition.clickId,
  };
}
